/**
 * Volugraph semantic search: OBB wireframe, fly-to, world→screen for HTML labels.
 */
import {Color, Vec3} from 'playcanvas';

import type {Events} from './events';
import type {Scene} from './scene';

type ObbPayload = {
    center: number[];
    axes: number[][];
    halfExtents: number[];
};

const tmp0 = new Vec3();
const tmp1 = new Vec3();
const tmp2 = new Vec3();
const tmp3 = new Vec3();

const EDGE_PAIRS: Array<[number, number]> = [
    [0, 1], [1, 3], [3, 2], [2, 0],
    [4, 5], [5, 7], [7, 6], [6, 4],
    [0, 4], [1, 5], [2, 6], [3, 7]
];

function obbCorners(obb: ObbPayload, out: Vec3[]): void {
    out.length = 0;
    const c = obb.center;
    const h = obb.halfExtents;
    const ax = obb.axes;
    const a0 = new Vec3(ax[0][0], ax[0][1], ax[0][2]);
    const a1 = new Vec3(ax[1][0], ax[1][1], ax[1][2]);
    const a2 = new Vec3(ax[2][0], ax[2][1], ax[2][2]);
    const center = new Vec3(c[0], c[1], c[2]);
    for (let i = 0; i < 8; i++) {
        const s0 = (i & 1) ? 1 : -1;
        const s1 = (i & 2) ? 1 : -1;
        const s2 = (i & 4) ? 1 : -1;
        const p = new Vec3();
        p.copy(center);
        p.add(a0.clone().mulScalar(s0 * h[0]));
        p.add(a1.clone().mulScalar(s1 * h[1]));
        p.add(a2.clone().mulScalar(s2 * h[2]));
        out.push(p);
    }
}

const cornerPool: Vec3[] = [];

function registerVolugraphSemantic(events: Events, scene: Scene): void {
    const corners: Vec3[] = cornerPool;
    let lastLabel = '';
    let lastObb: ObbPayload | null = null;
    const labelColor = new Color(0.2, 0.85, 1, 1);

    events.function('volugraph.projectWorldToScreen', (wx: number, wy: number, wz: number) => {
        const v = new Vec3(wx, wy, wz);
        const s = new Vec3();
        scene.camera.worldToScreen(v, s);
        return {x: s.x, y: s.y, z: s.z};
    });

    events.on('volugraph.semantic.highlightObject', (payload: { obb: ObbPayload; label?: string; gaussianIdsUrl?: string | null } | null) => {
        if (!payload?.obb) {
            lastObb = null;
            lastLabel = '';
            scene.forceRender = true;
            return;
        }
        lastObb = payload.obb;
        lastLabel = (payload.label || '').toString();
        scene.forceRender = true;
    });

    events.on('volugraph.semantic.clear', () => {
        lastObb = null;
        lastLabel = '';
        scene.forceRender = true;
    });

    events.on('volugraph.camera.flyToOBB', (payload: { obb: ObbPayload; durationMs?: number } | null) => {
        if (!payload?.obb) {
            return;
        }
        const {obb} = payload;
        obbCorners(obb, corners);
        if (corners.length < 8) {
            return;
        }
        const c = new Vec3(obb.center[0], obb.center[1], obb.center[2]);
        let maxD = 1e-6;
        for (const p of corners) {
            const d = p.distance(c);
            if (d > maxD) {
                maxD = d;
            }
        }
        const r0 = new Vec3(obb.axes[0][0], obb.axes[0][1], obb.axes[0][2]);
        const r1 = new Vec3(obb.axes[1][0], obb.axes[1][1], obb.axes[1][2]);
        const r2 = new Vec3(obb.axes[2][0], obb.axes[2][1], obb.axes[2][2]);
        r0.normalize();
        r1.normalize();
        r2.normalize();
        tmp0.copy(r0).mulScalar(0.6);
        tmp1.copy(r1).mulScalar(0.45);
        tmp2.copy(r2).mulScalar(0.55);
        tmp3.set(0, 0, 0);
        tmp3.add(tmp0).add(tmp1).add(tmp2);
        if (tmp3.length() < 1e-6) {
            tmp3.set(1, 0.5, 0.3);
        }
        tmp3.normalize();
        const dist = (maxD * 3.2) / Math.max(1e-6, scene.camera.fovFactor);
        const camPos = c.clone().add(tmp3.mulScalar(dist));
        const damp = 0.22;
        scene.camera.setPose(camPos, c, damp);
        scene.forceRender = true;
    });

    const drawOBB = () => {
        if (!lastObb) {
            return;
        }
        obbCorners(lastObb, corners);
        const worldLayer = scene.worldLayer;
        for (const [a, b] of EDGE_PAIRS) {
            const A = corners[a]!;
            const B = corners[b]!;
            scene.app.drawLine(A, B, labelColor, true, worldLayer);
        }
    };

    const emitLabelCorners = () => {
        if (!lastObb || !lastLabel) {
            return;
        }
        obbCorners(lastObb, corners);
        let minX = 1;
        let minY = 1;
        let minZ = 1;
        const scr = new Vec3();
        for (const w of corners) {
            scene.camera.worldToScreen(w, scr);
            minX = Math.min(minX, scr.x);
            minY = Math.min(minY, scr.y);
            minZ = Math.min(minZ, scr.z);
        }
        try {
            window.dispatchEvent(
                new CustomEvent('volugraph-semantic-label-corners', {
                    detail: {
                        label: lastLabel,
                        minX,
                        minY,
                        z: minZ
                    }
                })
            );
        } catch {
            /* ignore */
        }
    };

    scene.app.on('prerender', () => {
        drawOBB();
        emitLabelCorners();
    });
}

export { registerVolugraphSemantic, obbCorners, EDGE_PAIRS };
export type { ObbPayload };
